import client from './client';
import { getJobStatus } from './vistaBatchExport';
import { JobStartResponse, JobStatus, JobStatusEnum } from '../interfaces/V2Interface';

const waitFor = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const startPayrollApprovedSummaryReportJob = async (
  payPeriodID: number | string
): Promise<JobStartResponse> => {
  const response = await client.post('/admin/riceServices/reports/payrollApprovedSummaryReportJob', {
    payPeriodID,
  });
  return response.data as JobStartResponse;
};

const downloadPayrollApprovedSummaryReportJob = async (
  jobID: number
): Promise<{ blob: Blob; fileName: string | null }> => {
  const response = await client.get(
    `/admin/riceServices/reports/payrollApprovedSummaryReportJob/${jobID}/download`,
    { responseType: 'blob' }
  );
  const disposition: string | undefined = response.headers['content-disposition'];
  let fileName: string | null = null;
  if (disposition) {
    const match = disposition.match(/filename[^;=\n]*=(['"]?)([^'";\n]+)\1/);
    if (match) fileName = match[2].trim();
  }
  return { blob: response.data as Blob, fileName };
};

/**
 * Job-based flow (mirrors Print All Timesheets / TIMECARD_BATCH_PRINT_ALL):
 * start the PAYROLL_APPROVED_SUMMARY_REPORT job, poll the generic job-status
 * endpoint until COMPLETED/FAILED, then download the xlsx from the job.
 */
export const downloadPayrollApprovedSummaryReport = async (
  payPeriodID: number | string,
  fallbackFileName?: string,
  onProgress?: (jobStatus: JobStatus) => void
): Promise<void> => {
  const { jobID } = await startPayrollApprovedSummaryReportJob(payPeriodID);

  while (true) {
    const jobStatus = await getJobStatus(jobID);
    onProgress?.(jobStatus);

    if (jobStatus.status === JobStatusEnum.COMPLETED) {
      const { blob, fileName } = await downloadPayrollApprovedSummaryReportJob(jobID);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName ?? fallbackFileName ?? `PayrollApprovedSummary_${payPeriodID}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      return;
    }

    if (jobStatus.status === JobStatusEnum.FAILED) {
      const resultData = jobStatus.resultData as { error?: string; message?: string } | undefined;
      throw new Error(
        resultData?.error || resultData?.message || 'Payroll approved summary report generation failed.'
      );
    }

    await waitFor(3000);
  }
};
